/**
 * @fileoverview One cell of the student matrix: the same 36px status icon the teacher tracker draws
 * (shared STATUS_ICON / STATUS_COLOR), with a tooltip and an optional click that opens a student drawer.
 * A disabled cell (future lesson, finished project) keeps the status but ignores clicks.
 */

import { memo } from 'react'
import type { ReactNode } from 'react'
import { ActionIcon, Box, Text, Tooltip } from '@mantine/core'
import { STATUS_COLOR, STATUS_ICON } from 'pages/TaskTracker/components/TableCells'
import type { TrackStatus } from '../types'
import { StudentTrackerUploadMark } from './StudentTrackerUploadMark'

const EMPTY_DASH = <Text size='xs' color='dimmed'>—</Text>
const CELL_SIZE = 'lg'
const DISABLED_OPACITY = 0.5
/** Same as the teacher's deliverable tooltip: a 240px card, wrapping instead of stretching. */
const TOOLTIP_WIDTH = 240

interface StudentTrackerIconCellProps {
  status: TrackStatus
  /** False when the lesson has no item of this kind — renders a dash. */
  enabled?: boolean
  tooltip: ReactNode
  onClick?: () => void
  /** Keeps the status visible but blocks the click (future lesson / finished project). */
  disabled?: boolean
  uploadedByTeacher?: boolean
  multiline?: boolean
}

export const StudentTrackerIconCell = memo(function StudentTrackerIconCell({
  status,
  enabled = true,
  tooltip,
  onClick,
  disabled = false,
  uploadedByTeacher = false,
  multiline = false,
}: StudentTrackerIconCellProps) {
  if (!enabled) return EMPTY_DASH

  const clickable = Boolean(onClick) && !disabled

  return (
    <Tooltip
      label={tooltip}
      withArrow
      multiline={multiline}
      width={multiline ? TOOLTIP_WIDTH : undefined}
      styles={multiline ? { tooltip: { textAlign: 'left' } } : undefined}
    >
      <Box component='span' sx={{ display: 'inline-block', position: 'relative' }}>
        <ActionIcon
          variant='light'
          color={STATUS_COLOR[status]}
          size={CELL_SIZE}
          onClick={clickable ? onClick : undefined}
          aria-disabled={disabled || undefined}
          data-clickable={clickable || undefined}
          sx={{
            cursor: clickable ? 'pointer' : 'default',
            ...(disabled && { pointerEvents: 'none', opacity: DISABLED_OPACITY }),
          }}
        >
          {STATUS_ICON[status]}
        </ActionIcon>
        {status === 'completed' && uploadedByTeacher && <StudentTrackerUploadMark />}
      </Box>
    </Tooltip>
  )
})
